import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { assessmentFeatures } from '../data/mock';
import { Clock, FileQuestion, Lock, Camera, BarChart, GraduationCap, History, Check, Eye, AlertTriangle, MonitorSmartphone, FileText } from 'lucide-react';

const AssessmentsPage = () => {
  const featureIcons = [Clock, FileQuestion, Lock, Camera, BarChart, GraduationCap, History];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Prove</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-black leading-[0.95] mb-6 tracking-tight">
              Assessments that
              <br />
              <span className="text-neutral-400">actually assess.</span>
            </h1>
            <p className="text-xl text-neutral-500 leading-relaxed">
              Anyone can make a test. We made a system so sophisticated, 
              cheating becomes more effort than studying.
            </p>
          </div>
        </div>
      </section>

      {/* Assessment Features */}
      <section className="section bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Preview Card */}
            <div className="relative">
              <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-xl">
                <div className="bg-neutral-50 px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                  <div>
                    <h4 className="text-black font-medium">Technical Assessment</h4>
                    <p className="text-neutral-500 text-sm">JavaScript Mastery</p>
                  </div>
                  <div className="flex items-center gap-2 text-black">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">45:00</span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2 py-1 bg-black text-white text-xs font-medium rounded">Q1</span>
                    <span className="text-neutral-500 text-sm">Multiple Choice</span>
                  </div>
                  <p className="text-black mb-6">What is the output of the following code?</p>
                  <div className="bg-neutral-900 p-4 rounded-lg font-mono text-sm text-neutral-300 mb-6">
                    <code>console.log(typeof null);</code>
                  </div>
                  <div className="space-y-3">
                    {['"null"', '"undefined"', '"object"', '"boolean"'].map((opt, i) => (
                      <div key={i} className={`p-3 border ${i === 2 ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'} rounded-lg cursor-pointer transition-colors`}>
                        <span className="text-neutral-700">{opt}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-500 text-sm">Progress</span>
                    <span className="text-black text-sm font-medium">1/20</span>
                  </div>
                  <div className="w-full h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                    <div className="w-[5%] h-full bg-black rounded-full" />
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-black text-white px-4 py-2 rounded-lg font-medium text-sm shadow-lg">
                Guardian Active
              </div>
            </div>

            {/* Feature List */}
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-black mb-4">The Sandgen Prove Arsenal</h2>
              <p className="text-neutral-500 mb-8">Every feature, battle-tested. Every detail, considered.</p>
              <ul className="space-y-4">
                {assessmentFeatures.map((feature, index) => {
                  const Icon = featureIcons[index] || Check;
                  return (
                    <li key={index} className="flex items-center gap-4">
                      <span className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-neutral-600" />
                      </span>
                      <span className="text-neutral-700">{feature}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Sandgen Guardian */}
      <section className="section bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Guardian</span>
              <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
                Proctoring so advanced, <span className="text-neutral-400">it&apos;s almost unfair.</span>
              </h2>
              <p className="text-lg text-neutral-500 mb-10">
                We see everything. Tab switches. Browser tricks. Suspicious glances. 
                Integrity isn&apos;t optional—it&apos;s enforced.
              </p>
              
              <div className="space-y-8">
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Camera className="w-6 h-6 text-neutral-600" />
                  </div>
                  <div>
                    <h4 className="text-black font-semibold mb-2">Continuous Surveillance</h4>
                    <p className="text-neutral-500">AI-powered monitoring that catches what humans miss. Always watching. Always learning.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MonitorSmartphone className="w-6 h-6 text-neutral-600" />
                  </div>
                  <div>
                    <h4 className="text-black font-semibold mb-2">Total Lockdown</h4>
                    <p className="text-neutral-500">No tab switching. No copy-paste. No funny business. Period.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-neutral-600" />
                  </div>
                  <div>
                    <h4 className="text-black font-semibold mb-2">Forensic Logging</h4>
                    <p className="text-neutral-500">Every action timestamped. Every anomaly flagged. Complete accountability.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Proctor Display */}
            <div className="relative">
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl overflow-hidden">
                <div className="bg-white px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Eye className="w-5 h-5 text-neutral-600" />
                    <span className="text-black font-medium">Guardian Active</span>
                  </div>
                  <span className="px-3 py-1 bg-black text-white text-xs font-medium rounded-full">Recording</span>
                </div>
                <div className="p-8">
                  <div className="aspect-video bg-neutral-200 rounded-xl flex items-center justify-center mb-6">
                    <div className="text-center">
                      <Camera className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
                      <p className="text-neutral-500 text-sm">Live Feed</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {['Camera', 'Browser', 'Screen'].map((item) => (
                      <div key={item} className="bg-white border border-neutral-200 p-4 rounded-lg text-center">
                        <div className="w-3 h-3 bg-black rounded-full mx-auto mb-2" />
                        <span className="text-neutral-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white border border-neutral-200 rounded-xl p-5 shadow-xl max-w-xs">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-neutral-600 flex-shrink-0" />
                  <div>
                    <p className="text-black text-sm font-medium">Anomaly Detected</p>
                    <p className="text-neutral-500 text-xs">Tab switch at 14:23:45</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AssessmentsPage;
